const RECENTLY_VIEWED_KEY = 'recentlyViewed';
const MAX_ITEMS = 10;

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.container = this.querySelector('.container');
    this.grid = this.querySelector('ul.row');
  }

  static getStored() {
    try {
      return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || [];
    } catch {
      return [];
    }
  }

  static setStored(items) {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  }

  addCurrentProduct() {
    const handle = this.dataset.productHandle;
    const title = this.dataset.productTitle;
    if (!handle || !title) return;

    const items = RecentlyViewed.getStored();
    const filtered = items.filter((item) => item.handle !== handle);
    RecentlyViewed.setStored([{ handle, title }, ...filtered]);
  }

  getQueryAndHandles() {
    const raw = RecentlyViewed.getStored();
    const items = raw
      .map((item) => (typeof item === 'string' ? { handle: item, title: item } : item))
      .filter((item) => item && (item.title ?? item.handle));
    const titles = items.map((item) => `"${String(item.title ?? item.handle ?? '').replace(/"/g, '\\"')}"`).join(' OR ');
    const handles = items.map((item) => item.handle);
    return { query: titles, handles };
  }

  buildFetchUrl(query) {
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(MAX_ITEMS));
    params.set('resources[options][fields]', 'title');
    params.set('section_id', this.sectionId);
    return `${window.routes?.predictive_search_url || '/search/suggest'}?${params.toString()}`;
  }

  fetchAndRender() {
    const { query, handles } = this.getQueryAndHandles();
    if (!handles.length || !query.trim()) {
      this.hide();
      return;
    }

    fetch(this.buildFetchUrl(query))
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fetched = doc.querySelector('recently-viewed');
        if (!fetched || !this.container) return;

        const fetchedGrid = fetched.querySelector('ul.row');
        if (!fetchedGrid) {
          this.hide();
          return;
        }

        const items = Array.from(fetchedGrid.querySelectorAll('li[data-product-handle]'));
        const byHandle = new Map(items.map((li) => [li.dataset.productHandle, li]));
        const ordered = handles
          .map((handle) => byHandle.get(handle))
          .filter(Boolean);

        if (!ordered.length) {
          this.hide();
          return;
        }

        fetchedGrid.innerHTML = '';
        ordered.forEach((li) => fetchedGrid.appendChild(li));
        this.container.innerHTML = fetched.querySelector('.container').innerHTML;
        this.trimProductLinks();
      })
      .catch(() => this.hide());
  }

  trimProductLinks() {
    this.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      try {
        const path = a.pathname + (a.hash || '');
        if (path) a.setAttribute('href', path);
      } catch (_) {}
    });
  }

  hide() {
    const section = this.closest('.shopify-section');
    if (section) section.style.display = 'none';
  }

  connectedCallback() {
    this.addCurrentProduct();

    const { handles } = this.getQueryAndHandles();
    if (!handles.length) {
      this.hide();
      return;
    }

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      this.fetchAndRender();
    };

    new IntersectionObserver(handleIntersection, { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('recently-viewed', RecentlyViewed);
